import { useBrandLocale } from '../../hooks/useBrandLocale';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSnapshot } from 'valtio';
import { BookingStatus } from '../../../__generated__/graphql';
import { authStore } from '../../../store/authStore';
import { modalActions } from '../../../store/authStore/modalStore';
import { Button } from '../../components/button';
import { CardSetupForm } from '../../components/CardSetupForm';
import { Alert, LoadingBlock, Muted, Stack, Textarea } from '../../components/ui';
import { useStripePromise } from '../../hooks/useStripePromise';
import { useBookingsClosedPrompt } from '../../hooks/useBookingsOpen';
import { BookingsClosedModal } from '../../components/BookingsClosedModal';
import { CheckoutTerms } from '../../components/CheckoutTerms';
import { useCheckoutPolicies } from '../../hooks/useCheckoutPolicies';
import {
  isPolicyVersionOutdated,
  outdatedPolicyMessage,
} from '../../services/policyService/checkoutPolicies';
import {
  useCreateStayBookingIntentMutation,
  useCreateStayBookingRequestMutation,
  useStayBookingAsGuestQuery,
  useStayDetailQuery,
  useStayQuoteQuery,
  useSyncStayBookingRequestMutation,
  useVerifyStayPaymentMutation,
  type IStayQuoteFieldsFragment,
} from '../../services/stayService/__generated__/queries.generated';
import {
  useCreateRentalPaymentIntentMutation,
  useCreateSetupIntentForBookingMutation,
} from '../../services/stripeService/__generated__/queries.generated';
import { describeCheckoutPaymentError } from '../GuestCarPage/CheckoutPage/checkoutPaymentError';
import { stayErrorMessage } from '../StayHostPage/stayErrors';
import { dateRangeLabel, guestSummary, parseStaySearch } from '../StayGuestPage/staySearchState';
import { stayHeadline } from '../StayGuestPage/stayQuoteLines';
import { receiptQuote } from './stayBooking';
import {
  Columns,
  Detail,
  Page,
  PolicyText,
  PriceBreakdown,
  Section,
  SectionHeading,
  SideCard,
  StayBookingSummary,
  Title,
} from './StayBookingComponents';

interface Intent {
  bookingId: string;
  isRequest: boolean;
  quote: IStayQuoteFieldsFragment;
}

/**
 * Stay checkout (spec 016 W17) at `/stays/book/:id?checkIn&checkOut&adults…`. The
 * server prices the stay again and creates the booking; the card goes through the
 * spec 013 SetupIntent checkout. A request only holds the card until the host answers.
 */
export default function StayCheckoutPage() {
  const { id = '', bookingId: routeBookingId } = useParams<{ id: string; bookingId?: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useSnapshot(authStore);
  const search = useMemo(() => parseStaySearch(params), [params]);
  const { checkIn, checkOut, guests } = search;
  const hasDates = Boolean(checkIn && checkOut);

  const [message, setMessage] = useState('');
  const [intent, setIntent] = useState<Intent | null>(null);
  const [intentError, setIntentError] = useState<string | null>(null);
  const [cardKey, setCardKey] = useState(0);
  const [paying, setPaying] = useState(false);
  // Spec 023: "Bookings open soon" when the site is not taking bookings yet.
  const bookingsClosed = useBookingsClosedPrompt();
  const policies = useCheckoutPolicies('stay');
  const [termsChecked, setTermsChecked] = useState(false);

  const detail = useStayDetailQuery({ variables: { id }, skip: !id });
  const listing = detail.data?.stay;
  const stay = listing?.stayDetails;
  const quote = useStayQuoteQuery({
    variables: { id, input: { checkIn: checkIn ?? '', checkOut: checkOut ?? '', guests } },
    skip: !id || !hasDates || Boolean(intent),
    fetchPolicy: 'network-only',
  });
  // Back on `/stays/book/:id/:bookingId` (a reload): pick the booking up again.
  const existing = useStayBookingAsGuestQuery({
    variables: { id: routeBookingId ?? '' },
    skip: !routeBookingId || !isAuthenticated,
    fetchPolicy: 'network-only',
  });

  const [createIntent, { loading: creatingIntent }] = useCreateStayBookingIntentMutation();
  const [createRequest, { loading: creatingRequest }] = useCreateStayBookingRequestMutation();
  const [createSetupIntent] = useCreateSetupIntentForBookingMutation();
  const [createRentalPaymentIntent] = useCreateRentalPaymentIntentMutation();
  const [syncRequest] = useSyncStayBookingRequestMutation();
  const [verifyPayment] = useVerifyStayPaymentMutation();
  const { stripePromise } = useStripePromise();

  useEffect(() => {
    const booking = existing.data?.getBookingAsGuest;
    if (!booking || intent) return;
    if (booking.status === BookingStatus.Pending || booking.status === BookingStatus.CardDeclined) {
      setIntent({
        bookingId: booking.id,
        isRequest: Boolean(booking.requestExpiresAt),
        quote: receiptQuote(booking.receipt),
      });
      if (booking.requestMessage) setMessage(booking.requestMessage);
    } else {
      navigate(`/stays/guest/booking/${booking.id}`, { replace: true });
    }
  }, [existing.data, intent, navigate]);

  const backToPlace = `/stays/${id}?${params.toString()}`;
  const requestsOnly = stay ? !stay.instantBook : false;
  const isRequest = intent?.isRequest ?? requestsOnly;
  const shownQuote = intent?.quote ?? quote.data?.stayQuote ?? null;
  // Spec 028: the brand's currency until the stay is booked.
  const brandLocale = useBrandLocale();

  if (detail.loading && !listing) return <LoadingBlock label="Loading checkout" />;
  if (!listing || !stay) {
    return (
      <Page>
        <Alert tone="error" title="Place not found">
          This place is not available. <Link to="/">Find another place</Link>.
        </Alert>
      </Page>
    );
  }
  if (!hasDates) {
    return (
      <Page>
        <Alert tone="warning" title="Pick your dates">
          Choose check-in and checkout on the <Link to={backToPlace}>place page</Link> first.
        </Alert>
      </Page>
    );
  }

  const startCheckout = async () => {
    if (!isAuthenticated) {
      modalActions.openSignInModal(() => undefined);
      return;
    }
    if (requestsOnly && !message.trim()) {
      setIntentError('Add a message to the host with your request.');
      return;
    }
    setIntentError(null);
    const input = {
      listingId: id,
      checkIn: checkIn as string,
      checkOut: checkOut as string,
      guests,
      message: message.trim() || null,
    };
    try {
      const { data } = requestsOnly
        ? await createRequest({ variables: { input } })
        : await createIntent({ variables: { input } });
      const answer = data
        ? 'createStayBookingRequest' in data
          ? data.createStayBookingRequest
          : data.createStayBookingIntent
        : null;
      if (!answer) throw new Error('The booking could not be started');
      setIntent({ bookingId: answer.booking.id, isRequest: answer.isRequest, quote: answer.quote });
      navigate(`/stays/book/${id}/${answer.booking.id}?${params.toString()}`, { replace: true });
    } catch (e) {
      setIntentError(stayErrorMessage(e, 'The booking could not be started. Try again.'));
    }
  };

  const pay = async ({ paymentMethodId }: { paymentMethodId: string }) => {
    if (!intent) return;
    setPaying(true);
    try {
      const { data } = await createRentalPaymentIntent({
        variables: { input: { bookingId: intent.bookingId, paymentMethodId } },
      });
      const payment = data?.createRentalPaymentIntent;
      if (!payment) throw new Error('The payment could not be started');
      if (payment.requiresAction && payment.clientSecret) {
        const stripe = await stripePromise;
        if (!stripe) throw new Error('Payments are not ready yet. Please try again.');
        const { error } = await stripe.confirmCardPayment(payment.clientSecret);
        if (error) throw new Error(error.message || 'The bank did not approve this payment.');
      }
      if (intent.isRequest) {
        await syncRequest({ variables: { bookingId: intent.bookingId } });
        toast.success('Request sent. You will hear from the host within 24 hours.');
      } else {
        if (payment.booking?.status !== BookingStatus.Booked) {
          await verifyPayment({
            variables: { bookingId: intent.bookingId, paymentIntentId: payment.paymentIntentId },
          });
        }
        toast.success('Your stay is booked');
      }
      navigate(`/stays/guest/booking/${intent.bookingId}`, { replace: true });
    } catch (e) {
      const described = describeCheckoutPaymentError(e, 'The payment did not go through');
      if (described.bookingsClosed) {
        bookingsClosed.show(described.message);
      } else {
        toast.error(described.message, { autoClose: 8000 });
      }
      // The SetupIntent is spent either way: a fresh form lets the guest try again.
      setCardKey(key => key + 1);
    } finally {
      setPaying(false);
    }
  };

  const where = [listing.location?.city, listing.location?.state].filter(Boolean).join(', ');
  const rules = stay.houseRules;

  return (
    <Page>
      <Muted $size="sm">
        <Link to={backToPlace}>Back to the place</Link>
      </Muted>
      <Title>{isRequest ? 'Request to book' : 'Confirm and pay'}</Title>
      <Columns>
        <div>
          {isRequest && (
            <Alert tone="info" title="You won't be charged yet">
              The host has 24 hours to accept. Your card is only held until then, and the hold is
              released if the host declines or does not answer.
            </Alert>
          )}
          <Section>
            <SectionHeading>Your trip</SectionHeading>
            <Stack $gap={4}>
              <Detail label="Dates">{dateRangeLabel(checkIn, checkOut)}</Detail>
              <Detail label="Guests">{guestSummary(guests)}</Detail>
              {!intent && (
                <Muted $size="sm">
                  <Link to={backToPlace}>Change dates or guests</Link>
                </Muted>
              )}
            </Stack>
          </Section>

          <Section>
            <SectionHeading>Message the host</SectionHeading>
            <Muted $size="sm" style={{ marginBottom: 8 }}>
              {requestsOnly
                ? 'Required. Say who is coming and why you are traveling.'
                : 'Optional. Say hello and share your plans.'}
            </Muted>
            <Textarea
              rows={4}
              maxLength={1000}
              value={message}
              disabled={Boolean(intent)}
              onChange={e => setMessage(e.target.value)}
              placeholder={`Hi ${listing.owner?.firstName || listing.owner?.displayName || 'there'}! We are coming for…`}
              data-testid="stay-checkout-message"
            />
          </Section>

          <Section>
            <SectionHeading>Cancellation policy</SectionHeading>
            <PolicyText policy={stay.cancellationPolicy} />
          </Section>

          <Section>
            <SectionHeading>Ground rules</SectionHeading>
            <Stack $gap={4}>
              <Muted>Follow the house rules and treat the place like your own.</Muted>
              <Muted>
                {[
                  rules?.petsAllowed ? 'Pets allowed' : 'No pets',
                  rules?.smokingAllowed ? 'Smoking allowed' : 'No smoking',
                  rules?.eventsAllowed ? 'Events allowed' : 'No parties or events',
                ].join(' · ')}
              </Muted>
              <Muted>
                Check-in after {stay.checkInFrom}, checkout before {stay.checkOutBy}.
              </Muted>
            </Stack>
          </Section>

          <Section>
            <SectionHeading>Pay with</SectionHeading>
            {intentError && (
              <div style={{ marginBottom: 12 }}>
                <Alert tone="error" title="Cannot book">
                  {intentError}
                </Alert>
              </div>
            )}
            {!intent ? (
              <Button
                text={isAuthenticated ? 'Continue to payment' : 'Log in to book'}
                size="lg"
                fullWidth
                isLoading={creatingIntent || creatingRequest}
                isDisabled={!shownQuote || Boolean(quote.error)}
                onClick={startCheckout}
                dataTestId="stay-checkout-continue"
              />
            ) : (
              <>
                <CheckoutTerms
                  checked={termsChecked}
                  outdatedMessage={policies.outdatedMessage}
                  includeCancellation
                  onChange={checked => {
                    setTermsChecked(checked);
                    policies.clearOutdated();
                  }}
                />
                {termsChecked && (
                  <CardSetupForm
                    key={`${intent.bookingId}:${cardKey}`}
                    loadSetup={async () => {
                      try {
                        await policies.accept(intent.bookingId);
                      } catch (error) {
                        if (isPolicyVersionOutdated(error)) {
                          setTermsChecked(false);
                          throw new Error(outdatedPolicyMessage(error));
                        }
                        throw error;
                      }
                      const open = await bookingsClosed.beforeConfirm();
                      if (!open) throw new Error('This site is not taking bookings yet.');
                      const { data } = await createSetupIntent({
                        variables: { bookingId: intent.bookingId },
                      });
                      const setup = data?.createSetupIntentForBooking;
                      if (!setup?.clientSecret)
                        throw new Error('The card form could not be opened.');
                      return {
                        clientSecret: setup.clientSecret,
                        customerSessionClientSecret: setup.customerSessionClientSecret,
                      };
                    }}
                    onCardReady={pay}
                    beforeConfirm={bookingsClosed.beforeConfirm}
                    busy={paying}
                    submitLabel={intent.isRequest ? 'Request to book' : 'Confirm and pay'}
                    notice={
                      intent.isRequest
                        ? 'Your card is held, not charged, until the host accepts.'
                        : 'Your card is charged now. You can cancel under the policy above.'
                    }
                  />
                )}
              </>
            )}
          </Section>
        </div>

        <SideCard>
          <Stack $gap={4}>
            <StayBookingSummary
              name={listing.name}
              imageUrl={listing.images?.[0]?.imageUrl}
              subtitle={stayHeadline(stay.roomType, stay.propertyType, where)}
            />
            {quote.error && !intent ? (
              <Alert tone="warning" title="Not available">
                {stayErrorMessage(quote.error)}
              </Alert>
            ) : null}
            {shownQuote ? (
              <PriceBreakdown quote={shownQuote} currency={brandLocale.currency} />
            ) : (
              <LoadingBlock label="Pricing your stay" />
            )}
          </Stack>
        </SideCard>
      </Columns>
      <BookingsClosedModal message={bookingsClosed.message} onClose={bookingsClosed.close} />
    </Page>
  );
}
